import { domToCanvas } from "modern-screenshot";

/**
 * Quick Capture — DOM serialization via modern-screenshot.
 *
 * Fast, no permission dialog, works on every domain. Tradeoff:
 * complex CSS (Grid, transforms, sticky positioning, backdrop-blur)
 * may render with minor layout shifts since the DOM is re-painted
 * into an SVG foreignObject.
 */
export async function captureQuick(): Promise<HTMLCanvasElement> {
  return domToCanvas(document.documentElement, {
    scale: window.devicePixelRatio || 1,
    width: window.innerWidth,
    height: window.innerHeight,
    style: {
      transform: `translate(-${window.scrollX}px, -${window.scrollY}px)`,
    },
  });
}

/**
 * Perfect Capture — native browser screen capture via getDisplayMedia.
 *
 * Pixel-perfect screenshot of exactly what's on screen. Requires the
 * user to click "Share" in the browser's screen-share prompt (one
 * click, appears automatically). Works in Chrome, Edge, Firefox, and
 * Safari 16+.
 */
export async function capturePerfect(): Promise<HTMLCanvasElement> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: "browser" } as MediaTrackConstraints,
    audio: false,
    preferCurrentTab: true,
  } as DisplayMediaStreamOptions);

  const track = stream.getVideoTracks()[0];
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;

  await new Promise<void>((resolve) => {
    video.onloadedmetadata = () => {
      video.play();
      resolve();
    };
  });

  // Give the frame a moment to render
  await new Promise((r) => setTimeout(r, 100));

  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(video, 0, 0);

  // Stop the stream immediately — the permission prompt disappears
  track.stop();
  stream.getTracks().forEach((t) => t.stop());

  return canvas;
}

/** Check if Perfect Capture is available in this browser */
export function isPerfectCaptureAvailable(): boolean {
  return !!(
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    "getDisplayMedia" in navigator.mediaDevices
  );
}

// Keep backward-compatible default export
export const captureScreenshot = captureQuick;
